import * as R from 'ramda';

import {
  AttributesType,
  HydratedAttributeGroupsItem,
  InventoryAssetDetailType,
} from '@atom/types/inventory';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { AttributeView } from './AttributeDetailsContext';

export const getInitialExpanded = (
  asset: InventoryAssetDetailType,
): Set<string> => {
  const initialExpandedGroups = asset?.attributeGroups?.reduce((acc, group) => {
    return group.expandByDefault ? [...acc, group.id] : acc;
  }, []);

  return new Set(initialExpandedGroups);
};

export const getAttributeValue = (
  attribute: AttributesType,
  view: AttributeView,
) => {
  return !!attribute?.changeId
    ? attribute?.newValue
    : view === AttributeView.SCHEMA_PREVIEW
      ? attribute?.defaultValue
      : attribute?.value;
};

// Splits attributes into 2 column arrays, alternating order
// i.e. [1,2,3,4,5] --> [[1,3,5], [2,4]]
export const getSplitArrays = (attributeGroup: HydratedAttributeGroupsItem) => {
  const mappedDictionary = attributeGroup.attributes.reduce(
    (acc, attribute, index) => {
      const isEven = index % 2 === 0;

      return {
        left: isEven ? [...acc.left, attribute] : acc.left,
        right: !isEven ? [...acc.right, attribute] : acc.right,
      };
    },
    { left: [], right: [] },
  );

  return [mappedDictionary.left, mappedDictionary.right];
};

export const mapAttributeValue = (value: any, dataType: string): any => {
  switch (dataType) {
    case 'currency':
    case 'number': {
      return R.isEmpty(value) ? null : Number(value);
    }
    case 'date': {
      return R.isNil(value) ? value : value.valueOf();
    }
    case 'boolean': {
      return value;
    }
    default:
      return value;
  }
};

// Checks all values (edited or not) of attributes
// and returns an array attributeIds that are missing values (pending or not)
export const getErrorAttributeIds = (
  attributeValueMap: any,
  attributeGroup: any,
): string[] => {
  if (isNilOrEmpty(attributeGroup)) {
    return [];
  }

  const requiredValueMap = attributeGroup.attributes.reduce(
    (acc, attribute) => {
      const value = R.has(attribute.id, attributeValueMap)
        ? attributeValueMap[attribute.id]
        : attribute.value || attribute.newValue;

      return attribute.isRequired
        ? {
            ...acc,
            [attribute.id]: value,
          }
        : acc;
    },
    {},
  );

  return R.keys(requiredValueMap).reduce((acc, attributeId) => {
    const trimmedValue =
      R.type(requiredValueMap[attributeId]) === 'String'
        ? R.trim(requiredValueMap[attributeId])
        : requiredValueMap[attributeId];

    return isNilOrEmpty(trimmedValue) ? [...acc, attributeId] : acc;
  }, []);
};

// Checks all previous values of attributes
// and returns an array attributeIds that are missing values
export const getPreviousErrorAttributeIds = (attributeGroup: any): string[] => {
  if (isNilOrEmpty(attributeGroup)) {
    return [];
  }

  const requiredValueMap = attributeGroup.attributes.reduce(
    (acc, attribute) => {
      return attribute.isRequired
        ? {
            ...acc,
            [attribute.id]: attribute.value,
          }
        : acc;
    },
    {},
  );

  return R.keys(requiredValueMap).reduce((acc, attributeId) => {
    return isNilOrEmpty(requiredValueMap[attributeId])
      ? [...acc, attributeId]
      : acc;
  }, []);
};
